import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { EpTableComponent, EPTableConfig, LoadingSpinnerComponent } from '@ep/shared';

import { Store } from '@ngrx/store';
import { Order } from '@app/resources/services';
import { selectOrderQty, selectOrdersSortedByStartDate } from '@app/resources/ngrx/selectors';
import { TransactionModuleActions } from '@app/areas/transactions/store';
import { mapToTransactionTableDisplays } from '@app/areas/transactions/utils';
import { catchError, map, withLatestFrom, switchMap, tap } from 'rxjs/operators';
import { TransactionTableDisplay } from '@app/areas/transactions/models';
import { AsyncPipe, NgIf } from '@angular/common';
import { TransactionApiService } from '@app/areas/transactions/services';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrderActions } from '@app/resources/ngrx/actions';
import { TransactionLoadingService } from '../../services/transaction-loading.service';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { PortalTheme } from '@app/@shared/models';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';
import { ListPagination, ListPaginationService } from '@app/resources/services';

const DEFAULT_ROWS = 10 as const;

@Component({
  selector: 'app-transactions-table',
  standalone: true,
  imports: [EpTableComponent, AsyncPipe, LoadingSpinnerComponent, NgIf],
  template: `
    <div *ngIf="!(isLoading$ | async)" class="p-4">
      <app-ep-table
        [config]="config"
        [data]="transactions$ | async"
        (pageChange)="pageChangeEvent($event)"
        (globalSearch)="debounceSearch($event)"
        [totalRecords]="totalRecords"
        data-cy="transactions-table"
      />
    </div>
    <app-loading-spinner *ngIf="isLoading$ | async" [message]="'Loading...'" class="text-center" />
  `,
  styleUrl: './transactions-table.component.scss',
})
export class TransactionsTableComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly transactionsApiService = inject(TransactionApiService);
  private readonly destroyedRef: DestroyRef = inject(DestroyRef);
  private readonly loadingService = inject(TransactionLoadingService);
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly cssValidatorService = inject(CssValidatorService);
  private readonly listPagination = inject(ListPaginationService);

  private readonly first$ = new BehaviorSubject<number>(0);
  private readonly rows$ = new BehaviorSubject<number>(DEFAULT_ROWS);

  private searchDebounce: any;
  private lastSearch: string | null = '';
  private lastPageNumber: number = 1;
  private lastPageSize: number = DEFAULT_ROWS;
  private searchSubject = new Subject<string>();

  public isLoading$ = this.loadingService.isLoading$;

  @Input() public theme: PortalTheme | null = null;
  protected config!: EPTableConfig<TransactionTableDisplay>;

  protected totalRecords: number = 0;
  protected transactions$: Observable<TransactionTableDisplay[] | null> = of(null);

  public ngOnInit(): void {
    this.loadingService.setLoadingStatus(false);
    this.restorePagination();

    this.transactions$ = this.store.select(selectOrdersSortedByStartDate).pipe(
      withLatestFrom(this.applicationProfileService.applicationProfile$),
      tap(([orders, applicationProfile]) => {
        this.validateColumns(orders as unknown[]);
      }),
      map(([orders, applicationProfile]) => {
        return mapToTransactionTableDisplays(orders, applicationProfile.Location.UtcOffsetHour);
      })
    );

    this.fetchTransactions(this.lastPageNumber, this.lastPageSize);
    this.fetchTotalRecords();

    this.searchSubject
      .pipe(
        takeUntilDestroyed(this.destroyedRef),
        switchMap((search) => {
          return this.transactionsApiService
            .searchTransactions(this.lastPageNumber, this.lastPageSize, search)
            .pipe(catchError(() => of({ orders: [], totalTransactions: 0 })));
        })
      )
      .subscribe(({ orders, totalTransactions }) => {
        this.validateColumns(orders);
        if (orders.length > 0) {
          this.store.dispatch(OrderActions.loadOrdersSuccess({ payload: { orders, totalTransactions } }));
        } else {
          this.store.dispatch(OrderActions.loadOrdersSuccess({ payload: { orders: [], totalTransactions: 0 } }));
        }
      });

    this.initializeTableConfig();
    this.setCSSCustomProperties();
  }

  private restorePagination(): void {
    const { first, rows } = this.listPagination.read(DEFAULT_ROWS);

    this.first$.next(first);
    this.rows$.next(rows);
    this.lastPageNumber = Math.floor(first / rows) + 1;
    this.lastPageSize = rows;
  }

  private resetPagination(): void {
    this.lastPageNumber = 1;
    this.first$.next(0);
    this.listPagination.write({ first: 0, rows: this.lastPageSize }, DEFAULT_ROWS);
  }

  protected fetchTotalRecords(): void {
    this.store
      .select(selectOrderQty)
      .pipe(
        catchError(() => of()),
        takeUntilDestroyed(this.destroyedRef)
      )
      .subscribe((totalRecords: number) => {
        this.totalRecords = totalRecords;
      });
  }

  public pageChangeEvent(event: ListPagination): void {
    this.first$.next(event.first);
    this.rows$.next(event.rows);
    this.listPagination.write(event, DEFAULT_ROWS);

    this.lastPageNumber = event.first === 0 ? 1 : Math.floor(event.first / event.rows) + 1;
    this.lastPageSize = event.first === 1 ? 10 : event.rows;

    if (this.lastSearch) {
      this.searchTransactions(this.lastSearch);
    } else {
      this.fetchTransactions(this.lastPageNumber, this.lastPageSize);
    }
  }

  protected fetchTransactions(pageNumber: number, pageSize: number): void {
    this.transactionsApiService
      .getTransactions(pageNumber, pageSize)
      .pipe(
        catchError(() => of()),
        takeUntilDestroyed(this.destroyedRef)
      )
      .subscribe(({ orders, totalTransactions }) => {
        this.store.dispatch(OrderActions.loadOrdersSuccess({ payload: { orders, totalTransactions } }));
      });
  }

  protected debounceSearch(search: string): void {
    if (search === this.lastSearch) {
      return;
    }

    clearTimeout(this.searchDebounce);

    this.searchDebounce = setTimeout(() => {
      if (search === '') {
        if (this.lastSearch !== null) {
          this.resetPagination();
          this.fetchTransactions(1, this.lastPageSize);
        }
        this.lastSearch = null;
      } else {
        this.lastSearch = search;
        // Reset to page 1 when performing a new search
        this.resetPagination();
        this.searchTransactions(search);
      }
    }, 300);
  }

  public searchTransactions(search: string): void {
    if (search !== '') {
      this.searchSubject.next(search);
    }
  }

  protected mapOrderReviewStatusToValue(orderReview: TransactionTableDisplay): string {
    if (orderReview.Score === undefined) {
      return '';
    } else if (orderReview.Score === null) {
      return 'Not Answered';
    } else if (orderReview.Score === 0 || orderReview.Score === 1) {
      return 'Poor';
    } else if (orderReview.Score === 5) {
      return 'Great';
    } else {
      return 'Mixed';
    }
  }

  protected getOrderReviewStatusSeverity(orderReview: TransactionTableDisplay): string {
    if (orderReview.Score === undefined) {
      return 'empty';
    } else if (orderReview.Score === null) {
      return 'primary';
    } else if (orderReview.Score === 0 || orderReview.Score === 1) {
      return 'danger';
    } else if (orderReview.Score === 5) {
      return 'success';
    } else {
      return 'warning';
    }
  }

  private initializeTableConfig(columnConfig: 'normal' | 'referenceIdent' | 'allColumns' = 'normal'): void {
    const baseColumns = [
      {
        header: 'Date',
        field: 'StartDateTime',
        columnType: 'date',
        dateFormat: 'MM/dd/YY h:mm a',
        defaultValue: null,
        disableSorting: true,
      },
    ];

    const orderIdColumn = {
      header: 'Order Id',
      field: 'OrderGuid',
      columnType: 'string',
      defaultValue: '',
      disableSorting: true,
    };

    const paymentIdColumn = {
      header: 'Payment Id',
      field: 'ReferenceIdent',
      columnType: 'string',
      defaultValue: '',
      disableSorting: true,
    };

    const commonColumns = [
      {
        header: 'Employee',
        field: 'Employee',
        columnType: 'string',
        defaultValue: 'Missing employee',
        disableSorting: true,
      },
      {
        header: 'Status',
        field: 'StatusType',
        columnType: 'string',
        defaultValue: 'Open',
        disableSorting: true,
      },
      {
        header: 'Enrollment',
        field: 'EnrollmentType',
        columnType: 'string',
        defaultValue: 'Declined',
        disableSorting: true,
      },
      {
        header: 'Total',
        field: 'AmountTotal',
        columnType: 'price',
        defaultValue: 0,
        disableSorting: true,
      },
      {
        header: 'Rating',
        field: 'Score',
        columnType: 'categorical',
        defaultValue: 'Poor',
        disableSorting: true,
        fieldToValueMap: this.mapOrderReviewStatusToValue,
        severity: this.getOrderReviewStatusSeverity,
      },
    ];

    let columns: any[];
    switch (columnConfig) {
      case 'referenceIdent':
        columns = [...baseColumns, paymentIdColumn, ...commonColumns];
        break;
      case 'allColumns':
        columns = [...baseColumns, orderIdColumn, paymentIdColumn, ...commonColumns];
        break;
      case 'normal':
      default:
        columns = [...baseColumns, orderIdColumn, ...commonColumns];
        break;
    }

    this.config = {
      header: 'transactions',
      lazy: true,
      pagination: {
        rows: DEFAULT_ROWS,
        showCurrentPageReport: true,
      },
      first$: this.first$,
      rows$: this.rows$,
      columns,
      style: {
        'custom-header-background': this.theme?.table.header.backgroundColor || '',
        'custom-table-subheader-background': this.theme?.table.subHeaderBackgroundColor || '',
        'custom-table-subheader-text-color': this.theme?.table.subHeaderTextColor || '',
        'custom-status-primary-background': this.theme?.table.statusColors.primary || '',
        'custom-status-secondary-background': this.theme?.table.statusColors.secondary || '',
        'custom-status-danger-background': this.theme?.table.statusColors.danger || '',
        'custom-header-text-color': this.theme?.table.header.textColor || '',
        'custom-table-body-primary-background': this.theme?.table.body.primaryBackgroundColor || '',
        'custom-table-body-secondary-background': this.theme?.table.body.secondaryBackgroundColor || '',
        'custom-table-body-text-color': this.theme?.table.body.textColor || '',
      },
      globalFilterFields: ['Employee', 'NameLast', 'OrderGuid', 'AmountTotal'],
      actions: {
        rowClick: (transaction: Order) => {
          this.loadingService.setLoadingStatus(true);
          this.store.dispatch(
            TransactionModuleActions.navigateExistingTransaction({
              payload: { orderId: transaction.OrderId },
            })
          );
        },
      },
    };
  }

  private setCSSCustomProperties(): void {
    if (this.theme) {
      // Set CSS custom properties
      document.documentElement.style.setProperty(
        '--custom-header-background',
        this.theme?.table.header.backgroundColor
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-background',
        this.theme?.table.subHeaderBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-text-color',
        this.theme?.table.subHeaderTextColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-header-text-color',
        this.theme?.table.header.textColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-primary-background',
        this.theme?.table.body.primaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-secondary-background',
        this.theme?.table.body.secondaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-text-color',
        this.theme?.table.body.textColor || ''
      );

      // Check for undefined CSS variables
      this.cssValidatorService.checkUndefinedCssVariables('TransactionsTableComponent');
    }
  }

  private validateColumns(orders: Array<unknown>): void {
    if (!orders?.length) {
      this.initializeTableConfig('normal');
      return;
    }

    const orderList = orders as Order[];
    const hasOriginSDK = orderList.some((order) => order.OriginSDK === true);
    const hasNormalOrders = orderList.some((order) => order.OriginSDK !== true);

    if (hasOriginSDK && hasNormalOrders) {
      this.initializeTableConfig('allColumns');
    } else if (hasOriginSDK && !hasNormalOrders) {
      this.initializeTableConfig('referenceIdent');
    } else {
      this.initializeTableConfig('normal');
    }
  }
}
